import {
  SearchUserEnrichedTeam,
  SearchUserHit,
  User,
} from 'lib-shared-types-api';
import { useCallback, useEffect, useMemo, useState } from 'react';

import { AwayStatus } from 'apis/presence';
import { searchApi } from 'apis/search';
import { FavoritesPresenceListSection } from 'components/presence/components/favoritesPresenceListSection';
import { TeamPresenceListSections } from 'components/presence/components/teamPresenceListSections';
import { sortMembers } from 'components/presence/utils';
import { actions as personalActions } from 'components/settings/actions/personal';
import {
  useMembersWithPresence,
  useOrganizationPresenceSubscription,
  usePresenceOverlaySync,
} from 'hooks/usePresenceCacheSync';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { bindActionToPromise } from 'utils/sagaApiGeneratorHelpers';

export type TeamWithMembers = {
  id: string;
  name: string;
  members: SearchUserHit[];
};

type PresenceListSectionsProps = {
  profile: User;
  awayStatuses: AwayStatus[];
};

export const PresenceListSections = ({
  profile,
  awayStatuses,
}: PresenceListSectionsProps): JSX.Element => {
  useOrganizationPresenceSubscription();

  const dispatch = useAppDispatch();

  const { presenceSidebarShowSelf } = useFeatureFlag([
    'presence-sidebar-show-self',
  ]);

  const favouriteTeamMemberIds = useAppSelector(
    (state) => state.settings.personal.favouriteTeamMemberIds,
  );

  // Frozen favorites state - prevents list jumping while dropdown is open
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);

  const [frozenFavouriteIds, setFrozenFavouriteIds] = useState<string[]>(
    favouriteTeamMemberIds,
  );

  useEffect(() => {
    if (!isDropdownOpen) {
      setFrozenFavouriteIds(favouriteTeamMemberIds);
    }
  }, [favouriteTeamMemberIds, isDropdownOpen]);

  const { allMembers, teamIds, teamNames, membersLoading, membersFailed } =
    searchApi.useReadAdvancedSearchResultsQuery(
      {
        params: {
          scope: 'users',
          query: '',
          filters: {
            strategy: 'all',
            conditions: [
              {
                field: 'user_type',
                operator: 'is_one',
                values: [{ label: '', value: 'member' }],
              },
            ],
          },
          strict: true,
          page: 1,
          page_size: 1000,
          enrichmentSpec: ['teams'],
        },
      },
      {
        selectFromResult: ({ data, isLoading, isError }) => {
          const userResult = data?.data.results.find(
            (r) => r.hits_type === 'user',
          );
          const hits = userResult?.hits ?? [];
          const me = hits.find((h) => h.id === profile.id);
          const enrichedTeams = (me?.teams ?? []) as SearchUserEnrichedTeam[];
          const activeTeams = enrichedTeams.filter((t) => !t.is_deactivated);
          return {
            allMembers: hits,
            teamIds: activeTeams.map((t) => t.id),
            teamNames: activeTeams.reduce(
              (acc, t) => ({ ...acc, [t.id]: t.name }),
              {} as Record<string, string>,
            ),
            membersLoading: isLoading,
            membersFailed: isError,
          };
        },
      },
    );

  usePresenceOverlaySync();
  const membersWithPresence = useMembersWithPresence(allMembers);

  const favoriteMembers = useMemo(() => {
    const favorites = membersWithPresence.filter(
      (m) => m.id === profile.id || frozenFavouriteIds.includes(m.id),
    );
    const sorted = sortMembers(favorites);
    const me = sorted.find((m) => m.id === profile.id);
    const others = sorted.filter((m) => m.id !== profile.id);
    return me ? [me, ...others] : others;
  }, [membersWithPresence, profile.id, frozenFavouriteIds]);

  const teamsWithMembers: TeamWithMembers[] = useMemo(
    () =>
      teamIds
        .map((teamId, index) => ({
          id: teamId,
          name: teamNames[teamId] ?? `Team ${index + 1}`,
          members: sortMembers(
            membersWithPresence.filter(
              (member) =>
                member.teams?.some(
                  (t) => (typeof t === 'string' ? t : t.id) === teamId,
                ) &&
                (presenceSidebarShowSelf || member.id !== profile.id),
            ),
          ),
        }))
        .filter((team) => team.members.length > 0),
    [
      teamIds,
      teamNames,
      membersWithPresence,
      profile.id,
      presenceSidebarShowSelf,
    ],
  );

  const isLoading = membersLoading;
  const isError = membersFailed;

  const handleAddToFavorites = useCallback(
    (userId: string) => {
      bindActionToPromise(
        dispatch,
        personalActions.personalAddToFavouriteTeamMemberIds,
      )({ agentId: userId });
    },
    [dispatch],
  );

  const handleRemoveFromFavorites = useCallback(
    (userId: string) => {
      bindActionToPromise(
        dispatch,
        personalActions.personalRemoveFromFavouriteTeamMemberIds,
      )({ agentId: userId });
    },
    [dispatch],
  );

  return (
    <>
      <FavoritesPresenceListSection
        profile={profile}
        awayStatuses={awayStatuses}
        members={favoriteMembers}
        loading={isLoading}
        favouriteTeamMemberIds={favouriteTeamMemberIds}
        onAddToFavorites={handleAddToFavorites}
        onRemoveFromFavorites={handleRemoveFromFavorites}
        onDropdownOpenChange={setIsDropdownOpen}
      />
      <TeamPresenceListSections
        profile={profile}
        awayStatuses={awayStatuses}
        teams={teamsWithMembers}
        loading={isLoading}
        error={isError}
        favouriteTeamMemberIds={favouriteTeamMemberIds}
        onAddToFavorites={handleAddToFavorites}
        onRemoveFromFavorites={handleRemoveFromFavorites}
      />
    </>
  );
};
